/* Inline "Your advisor sessions" panel.
   Lives just under the "Talk to a Clinq advisor" CTA on every builder page.

   Shows the founder:
     • their session count
     • whether their NEXT session is free (1st) or KES 12,000 (follow-on)
     • any outstanding invoice with a one-click "Pay KES 12,000" Paystack button

   Quietly hides itself if the user has zero sessions AND no outstanding invoice —
   we don't want to surface pricing noise to founders who haven't even met an
   advisor yet (the AdvisorModal already covers that messaging upfront). */

import { useEffect, useState } from "react";
import { ChatCircleText, CheckCircle, CurrencyDollar, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const C = {
  bg: "#0A2826",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
  green: "#34D399",
};

export default function AdvisorSessionsInline({ compact = false }) {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: res } = await api.get("/advisor/sessions/me");
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setData({ sessions: [], completed_count: 0, outstanding: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const hasHistory = (data.completed_count || 0) > 0;
  const outstanding = data.outstanding || [];
  if (!hasHistory && outstanding.length === 0) return null;

  const payInvoice = async (s) => {
    if (!s?.checkout_url) {
      toast.error("Invoice link unavailable — contact hello@clinq.africa.");
      return;
    }
    setPaying(s.session_id);
    // Open in a new tab so the founder doesn't lose the builder context
    window.open(s.checkout_url, "_blank", "noopener,noreferrer");
    setTimeout(() => setPaying(null), 800);
  };

  return (
    <div className="rounded-lg p-4"
      data-testid="advisor-sessions-inline"
      style={{ backgroundColor: "rgba(10, 139, 133, 0.07)", border: "1px solid rgba(10, 139, 133, 0.22)" }}>
      <div className="flex items-center gap-2 mb-2">
        <ChatCircleText size={14} weight="fill" style={{ color: C.tealSoft }} />
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: C.tealSoft }}>
          Your advisor sessions
        </div>
        <div className="ml-auto text-[11px] font-mono-num text-white/55"
          data-testid="advisor-sessions-count">
          {data.completed_count} completed
        </div>
      </div>

      {/* Outstanding invoices first */}
      {outstanding.map((s) => (
        <div key={s.session_id} className="mt-2 rounded-md p-3 flex items-center gap-3"
          style={{ backgroundColor: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.32)" }}
          data-testid="advisor-outstanding-invoice">
          <div className="shrink-0">
            <span className="text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: C.orange, color: "#1a1206" }}>
              Due
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-white truncate">
              KES {Number(s.amount_kes || 0).toLocaleString()}
            </div>
            <div className="text-[11px] text-white/55 mt-0.5">
              {s.duration_minutes}-min follow-on · {timeAgo(s.completed_at)}
            </div>
          </div>
          <button onClick={() => payInvoice(s)} disabled={paying === s.session_id}
            data-testid="advisor-pay-invoice-btn"
            className="text-[11px] font-bold px-3 py-2 rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
            style={{ backgroundColor: C.orange, color: "#1a1206" }}>
            Pay KES {Number(s.amount_kes || 0).toLocaleString()}
            <ArrowSquareOut size={10} weight="bold" />
          </button>
        </div>
      ))}

      {/* Next-session indicator */}
      {data.next_session && (
        <div className="mt-2 flex items-center gap-2 text-[11.5px] text-white/65"
          data-testid="advisor-next-session-line">
          {data.next_session.is_free ? (
            <>
              <CheckCircle size={12} weight="fill" style={{ color: C.green }} />
              Your <span className="font-semibold text-white">first 30-min session is free</span>.
            </>
          ) : (
            <>
              <CurrencyDollar size={12} weight="fill" style={{ color: C.orange }} />
              Next session:{" "}
              <span className="font-semibold text-white">
                KES&nbsp;{Number(data.next_session.amount_kes || 0).toLocaleString()}
              </span>
              <span className="text-white/45">/ {data.next_session.duration_minutes} min</span>
            </>
          )}
        </div>
      )}

      {/* Recent session strip — only when not compact */}
      {!compact && hasHistory && (
        <div className="mt-3 pt-3 space-y-1.5 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          {(data.sessions || []).slice(0, 3).map((s) => (
            <div key={s.session_id} className="flex items-center gap-2 text-[11px]">
              <StatusDot status={s.status} />
              <span className="text-white/65 capitalize">{(s.service || "general").replace("_", " ")}</span>
              <span className="text-white/35 ml-auto">{timeAgo(s.completed_at)}</span>
              <span className="text-white/50 font-mono-num">
                {s.status === "completed_free" ? "Free" : `KES ${Number(s.amount_kes || 0).toLocaleString()}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    completed_free: { color: C.green, label: "Free" },
    paid: { color: C.tealSoft, label: "Paid" },
    invoice_sent: { color: C.orange, label: "Due" },
  };
  const m = map[status] || { color: "rgba(255,255,255,0.4)", label: status };
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} />
      <span className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: m.color }}>{m.label}</span>
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d ago`;
  return d.toLocaleDateString();
}
